import React, { useState } from 'react';
import {
  Table, Thead, Tbody, Tr, Th, Td, Button, IconButton, Flex,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  useDisclosure, FormControl, FormLabel, Input, Select, Textarea, Badge, useToast,
  TableContainer, Text, Box,
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon, AddIcon } from '@chakra-ui/icons';
import { createService, updateService, deleteService } from '../Utilities/networks';
import PageShell from './ui/PageShell';
import DataCard from './ui/DataCard';

const Services = ({ servicesList = [], onServicesChange }) => {
  const [editingService, setEditingService] = useState(null);
  const [deletingService, setDeletingService] = useState(null);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newStatus, setNewStatus] = useState('Active');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isOpenDelete, onOpen: onOpenDelete, onClose: onCloseDelete } = useDisclosure();
  const toast = useToast();

  const refreshServices = async () => {
    if (onServicesChange) {
      await onServicesChange();
    }
  };

  const resetForm = () => {
    setNewName('');
    setNewDescription('');
    setNewStatus('Active');
    setEditingService(null);
  };

  const handleAdd = () => {
    resetForm();
    onOpen();
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setNewName(service.name);
    setNewDescription(service.description || '');
    setNewStatus(service.status || 'Active');
    onOpen();
  };

  const handleSave = async () => {
    if (!newName.trim()) {
      toast({ title: 'Service name is required', status: 'warning', duration: 3000 });
      return;
    }
    try {
      const payload = { name: newName.trim(), description: newDescription.trim(), status: newStatus };
      if (editingService) {
        await updateService({ ...payload, id: editingService.id });
        toast({ title: 'Service updated', status: 'success', duration: 3000 });
      } else {
        await createService(payload);
        toast({ title: 'Service added', status: 'success', duration: 3000 });
      }
      await refreshServices();
      resetForm();
      onClose();
    } catch (error) {
      toast({ title: error.response?.data?.message || 'Failed to save service', status: 'error', duration: 4000 });
    }
  };

  const handleDeleteClick = (service) => {
    setDeletingService(service);
    onOpenDelete();
  };

  const confirmDelete = async () => {
    if (!deletingService) return;
    try {
      await deleteService(deletingService.id);
      toast({ title: 'Service deleted', status: 'success', duration: 3000 });
      await refreshServices();
    } catch (error) {
      toast({ title: error.response?.data?.message || 'Failed to delete service', status: 'error', duration: 4000 });
    }
    setDeletingService(null);
    onCloseDelete();
  };

  return (
    <Box>
      <PageShell
        title="Services"
        subtitle="Manage service types shown on complaints and WhatsApp rating messages"
        action={
          <Button leftIcon={<AddIcon />} colorScheme="brand" borderRadius="lg" onClick={handleAdd}>
            Add Service
          </Button>
        }
      />

      <DataCard>
        <TableContainer>
          <Table variant="modern" size="sm">
            <Thead>
              <Tr>
                <Th>ID</Th>
                <Th>Service Name</Th>
                <Th>Description</Th>
                <Th>Status</Th>
                <Th textAlign="right">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {servicesList.map((service) => (
                <Tr key={service.id}>
                  <Td fontWeight="600" color="slate.800">#{service.id}</Td>
                  <Td fontWeight="500">{service.name}</Td>
                  <Td maxW="320px" isTruncated title={service.description}>{service.description || '-'}</Td>
                  <Td>
                    <Badge colorScheme={service.status === 'Active' ? 'green' : 'slate'} variant="subtle">
                      {service.status}
                    </Badge>
                  </Td>
                  <Td textAlign="right">
                    <Flex justify="flex-end" gap={1}>
                      <IconButton aria-label="Edit" icon={<EditIcon />} size="sm" variant="ghost" colorScheme="brand" onClick={() => handleEdit(service)} />
                      <IconButton aria-label="Delete" icon={<DeleteIcon />} size="sm" variant="ghost" colorScheme="red" onClick={() => handleDeleteClick(service)} />
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
        {servicesList.length === 0 && (
          <Box py={12} textAlign="center">
            <Text color="slate.500" fontSize="sm">No services yet. Add your first service type.</Text>
          </Box>
        )}
      </DataCard>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="2xl" mx={4}>
          <ModalHeader fontFamily="heading">{editingService ? 'Edit Service' : 'Add Service'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={4} isRequired>
              <FormLabel fontSize="sm" fontWeight="600" color="slate.600">Service Name</FormLabel>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. AC Repair" borderRadius="lg" />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel fontSize="sm" fontWeight="600" color="slate.600">Description</FormLabel>
              <Textarea value={newDescription} onChange={(e) => setNewDescription(e.target.value)} placeholder="Shown in customer rating messages" borderRadius="lg" />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="600" color="slate.600">Status</FormLabel>
              <Select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} borderRadius="lg">
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </Select>
            </FormControl>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button colorScheme="brand" onClick={handleSave}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isOpenDelete} onClose={onCloseDelete}>
        <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="2xl" mx={4}>
          <ModalHeader fontFamily="heading">Delete Service</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Delete <strong>{deletingService?.name}</strong>? This cannot be undone.
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={onCloseDelete}>Cancel</Button>
            <Button colorScheme="red" onClick={confirmDelete}>Delete</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Services;
